import {DrupalNode} from "next-drupal";
import SectionHeading from "../../organisms/section-heading";
import {ReactElement, ReactNode, useContext, useEffect, useRef, useState} from "react";
import {Body} from "../../atoms/body";
import dynamic from "next/dynamic";
import {Dictionary, DrupalProductNode, WorkflowType} from "@/types/hygiena-types";
import {ProductSectionSampleCollection} from "@/components/organisms/product-sections";
import {ChevronRight} from "lucide-react";
import {DictionaryContext} from "@/context/dictionary-context";
const FormattedText = dynamic(() => import("../../formatted-text").then((mod) => mod.FormattedText));

const ProductSectionEquipment = dynamic(() => import("../../organisms/product-sections").then((mod) => mod.ProductSectionEquipment));
const ProductSectionInstruments = dynamic(() => import("../../organisms/product-sections").then((mod) => mod.ProductSectionInstruments));
const ProductSectionAutomation = dynamic(() => import("../../organisms/product-sections").then((mod) => mod.ProductSectionAutomation));
const ProductSectionMonitoring = dynamic(() => import("../../organisms/product-sections").then((mod) => mod.ProductSectionMonitoring));
const ProductSectionSoftware = dynamic(() => import("../../organisms/product-sections").then((mod) => mod.ProductSectionSoftware));
const ProductSectionRealAdditionals = dynamic(() => import("../../organisms/product-sections").then((mod) => mod.ProductSectionRealAdditionals));
const ProductSectionAdditionals = dynamic(() => import("../../organisms/product-sections").then((mod) => mod.ProductSectionAdditionals));
const ProductSectionMedia = dynamic(() => import("../../organisms/product-sections").then((mod) => mod.ProductSectionMedia));
const ProductSectionExtractions = dynamic(() => import("../../organisms/product-sections").then((mod) => mod.ProductSectionExtractions));

interface NodeWorkflowPros {
  node: DrupalProductNode;
  workflowType?: string;
  title?: string;
  isFoodproofWorkflow: boolean;
}

/**
 * Displays an individual workflow item.
 *
 * @param position
 * @param productNode
 * @param workflowItem
 * @param isFoodproofWorkflow
 * @param isActive
 * @constructor
 */
function NodeWorkflowItem({
  position,
  productNode,
  workflowItem,
  isFoodproofWorkflow,
  isActive = false,
}: {
  position: number,
  productNode: DrupalProductNode,
  workflowItem: DrupalNode,
  isFoodproofWorkflow: boolean;
  isActive: boolean;
}): ReactElement {
  const [active, setActive] = useState<boolean>(isActive);
  const sectionHeaderSize = productNode?.field_product_workflow?.field_workflow_items?.length ? 3 : 2;
  const subProductHeaderSize = sectionHeaderSize === 3 ? 4 : 3;
  const t = useContext<Dictionary>(DictionaryContext);
  // Convert the product type to an array if it's still using the older single cardinality type.
  const productType = Array.isArray(workflowItem?.field_workflow_item_product_type)
    ? workflowItem.field_workflow_item_product_type
    : (workflowItem?.field_workflow_item_product_type?.length ? [workflowItem.field_workflow_item_product_type] : []);

  const Wrapper = ({children}: {children?: ReactNode|null|undefined}) => {
    const ref = useRef<HTMLDivElement>(null);
    const [hasChildren, setHasChildren] = useState<boolean>(false);

    useEffect(() => {
      // Initial check
      if (ref?.current) {
        setHasChildren(ref.current.hasChildNodes());
      }

      // Add a slight delay to check again
      const timer = setTimeout(() => {
        if (ref?.current) {
          setHasChildren(ref.current.hasChildNodes());
        }
      }, 100);

      return () => clearTimeout(timer);
    }, [children]);

    return (
      <div className="workflow-item-wrapper flex flex-wrap flex-col gap-8 [&_h3]:!text-2xl [&_h4]:!text-2xl">
        <div
          onClick={() => setActive(!active)}
          className={`flex relative p-4 gap-4 items-center bg-white drop-shadow-sm rounded-md ${hasChildren && 'hover:drop-shadow cursor-pointer'}`}>
          <div className="relative bg-primary h-12 w-12 text-white text-xl py-2 px-5 rounded-full">
            <span className="absolute font-semibold flex inset-0 items-center justify-center">{position}</span>
          </div>
          <div className="flex flex-col flex-grow">
            <div className="text-2xl font-semibold">{workflowItem.name}</div>
            {workflowItem?.field_workflow_item_text && (
              <div className="text-black/80">
                {workflowItem.field_workflow_item_text}
              </div>
            )}
          </div>
          <div className="flex gap-4">
            {workflowItem.field_workflow_item_optional && (
              <span>({`${t.all.optional}`})</span>
            )}
            <ChevronRight className={`h-6 w-6 text-black ${active && 'rotate-90'} ${!hasChildren && 'hidden'}`} />
          </div>
        </div>
        <div className={`${(!active || !hasChildren) && 'hidden'} px-8 flex flex-col gap-8`} ref={ref}>
          {children}
        </div>
      </div>
    )
  }

  if (!productType?.length) {
    return <></>
  }

  return (
    <Wrapper>
      {productType.map((item: string) => {
        switch (item) {
          case WorkflowType.Additionals:
            return (
              <ProductSectionRealAdditionals
                node={productNode}
                sectionHeaderSize={sectionHeaderSize}
                subProductHeaderSize={subProductHeaderSize}
                isWorkflow={true}
              />
            )
          case WorkflowType.Automation:
            return (
              <ProductSectionAutomation
                node={productNode}
                sectionHeaderSize={sectionHeaderSize}
                subProductHeaderSize={subProductHeaderSize}
                isWorkflow={true}
              />
            )
          case WorkflowType.Equipment:
            return (
              <ProductSectionEquipment
                node={productNode}
                sectionHeaderSize={sectionHeaderSize}
                subProductHeaderSize={subProductHeaderSize}
                isWorkflow={true}
              />
            )
          case WorkflowType.Extractions:
            return (
              <ProductSectionExtractions
                node={productNode}
                sectionHeaderSize={sectionHeaderSize}
                subProductHeaderSize={subProductHeaderSize}
                isWorkflow={true}
              />
            )
          case WorkflowType.Instruments:
            return (
              <ProductSectionInstruments
                node={productNode}
                sectionHeaderSize={sectionHeaderSize}
                subProductHeaderSize={subProductHeaderSize}
                isFoodproofWorkflow={isFoodproofWorkflow}
                isWorkflow={true}
              />
            )
          case WorkflowType.Media:
            return (
              <ProductSectionMedia
                node={productNode}
                sectionHeaderSize={sectionHeaderSize}
                subProductHeaderSize={subProductHeaderSize}
                isWorkflow={true}
              />
            )
          case WorkflowType.Monitoring:
            return (
              <ProductSectionMonitoring
                node={productNode}
                sectionHeaderSize={sectionHeaderSize}
                subProductHeaderSize={subProductHeaderSize}
                isWorkflow={true}
              />
            )
          case WorkflowType.SampleCollection:
            return (
              <ProductSectionSampleCollection
                node={productNode}
                sectionHeaderSize={sectionHeaderSize}
                isWorkflow={true}
              />
            )
          case WorkflowType.Software:
            return (
              <ProductSectionSoftware
                node={productNode}
                sectionHeaderSize={sectionHeaderSize}
                subProductHeaderSize={subProductHeaderSize}
                isWorkflow={true}
              />
            )
          default:
            return <></>
        }
      })}
    </Wrapper>
  );
}

/**
 * Displays the product node workflow, if available, as an accordion of items.
 *
 * @param node
 * @param workflowType
 * @param isFoodproofWorkflow
 * @constructor
 */
export function NodeWorkflow({
  node,
  workflowType,
  isFoodproofWorkflow,
}: NodeWorkflowPros): ReactElement {
  const workflowNode = node?.field_product_workflow;
  let workflowItems = workflowNode?.field_workflow_items ?? [];

  return (
    <>
      {(workflowNode && workflowItems?.length > 1) ? (
        <>
          <SectionHeading
            title={workflowNode.title}
            link={workflowNode?.field_workflow_header_link}
            displayTitle={workflowNode?.field_workflow_display_title?.processed}
          >
            {workflowNode?.body?.processed && (
              <Body bodyType={"lg"} className="[&_p]:m-0">
                <FormattedText processed={workflowNode.body.processed} />
              </Body>
            )}
          </SectionHeading>
          {/* Color Compensation (Note: This was previously named additionals, before actual additionals were added.)*/}
          <ProductSectionAdditionals node={node}/>
          <div className="flex flex-wrap flex-col gap-4 pb-12 mb-8 md:mb-20">
            {workflowItems.map((item: DrupalNode, i) => {
              const isActive = Array.isArray(item?.field_workflow_item_product_type)
                ? item?.field_workflow_item_product_type?.includes(node?.field_product_workflow_type)
                : item?.field_workflow_item_product_type === node?.field_product_workflow_type;
              return (
                <NodeWorkflowItem
                  key={`workflow-${item.id}-${i}`}
                  position={i + 1}
                  productNode={node}
                  workflowItem={item}
                  isFoodproofWorkflow={isFoodproofWorkflow}
                  isActive={isActive}
                />
              );
            })}
          </div>
        </>
      ) : <></>}
    </>
  )
}